import { useEffect, useMemo, useState } from 'react';
import { t, tn } from '@app/i18n';
import {
  Badge,
  Button,
  Card,
  Chip,
  ConfirmDialog,
  DataTable,
  DrawerPresence,
  IconButton,
  PageHeader,
  useToast,
  type DataTableColumn,
} from '@zarya/ui';
import { useCanOperar } from '@app/auth/useCan';
import { errorView } from '@app/errors';
import { dataHour } from '@app/format';
import { useDrawerParam } from '@app/navigation';
import { useNavigate } from 'react-router-dom';
import {
  useCreateSite,
  useDeleteSite,
  useSites,
  useTestSiteConnection,
  useUpdateSite,
} from './Sites.api';
import {
  canDelete,
  forCreate,
  forUpdate,
  lowers,
  matches,
  POSTURES_FALLBACK,
  postureLabel,
  statusLabel,
  type SiteFormValues,
  type SiteItem,
} from './SiteModel';
import { SiteDrawer } from './SiteDrawer';
import { SiteConnectionDialog } from './SiteConnectionDialog';
import { useAdmissionSummaries } from './admission/SiteAdmission.api';
import { AdmissionChip } from './admission/AdmissionChip';
import s from './Sites.module.css';

/**
 * Tela «Sites» (P1, grupo Controle).
 *
 * O cadastro dos clusters de um tenant. Um site é onde um workload roda — o
 * executor, o APIM, o PDP — e é a âncora do endereçamento (P7): o recurso
 * carrega o site, a identidade carrega o site, e o SPIFFE ID sai do template DO
 * SITE.
 *
 * # A LISTA NÃO SONDA
 *
 * A coluna «saúde» mostra a ÚLTIMA sondagem persistida (`site.last_probe_*`), e
 * `null` é **«nunca testado»** — que a tela desenha diferente de «testado e
 * falhou». Sondar ao montar a página dispararia N chamadas HTTP a clusters
 * distintos e faria a tela depender de todos estarem de pé: um data center
 * distante fora do ar deixaria de ser uma linha amarela e passaria a ser uma
 * página que não abre. Quem sonda é o «Testar conexão», sob demanda.
 */
export function SitesPage() {
  const [term, setTerm] = useState('');
  const [toDelete, setToDelete] = useState<SiteItem | null>(null);
  const [testing, setTesting] = useState<SiteItem | null>(null);
  // Os valores do formulário à espera da confirmação de DESCIDA de postura. Guardar
  // os valores (e não só a postura) é o que permite salvar o resto do formulário
  // junto: a pessoa mexeu em nome e postura no mesmo Salvar, e uma confirmação que
  // gravasse só a postura perderia o nome sem dizer.
  const [lowering, setLowering] = useState<SiteFormValues | null>(null);

  const drawer = useDrawerParam();
  const toast = useToast();

  const list = useSites();
  const create = useCreateSite();
  const edit = useUpdateSite();
  const remove = useDeleteSite();
  const test = useTestSiteConnection();

  const canCreate = useCanOperar('createSite');
  const canEdit = useCanOperar('updateSite');
  const canRemove = useCanOperar('deleteSite');
  const canTest = useCanOperar('testSiteConnection');
  // aim-xsk.8 — a coluna «Admissão» e a ação que abre a página. Só o administrador vê (protótipo); para os outros
  // papéis a coluna não existe, em vez de existir vazia.
  const canAdmission = useCanOperar('getSiteAdmission');
  const admissions = useAdmissionSummaries(canAdmission);
  const navigate = useNavigate();

  // `useMemo` no `?? []` também, e não só no filtro: o literal vazio é um objeto
  // novo a cada render, então o memo do filtro se invalidaria sempre — é o aviso
  // do `react-hooks/exhaustive-deps`, e ele está certo.
  const items = useMemo(() => list.data?.items ?? [], [list.data]);
  // O vocabulário de postura vem do servidor; a constante é só o que a tela
  // desenha enquanto a primeira leitura não chegou.
  const postures = list.data?.postures ?? POSTURES_FALLBACK;
  const templateDefault = list.data?.template_default ?? '';

  const rows = useMemo(() => items.filter((site) => matches(site, term)), [items, term]);

  const editingId = drawer.id && drawer.id !== 'novo' ? drawer.id : null;
  // Não há `useSite(id)`: a coleção não pagina, então a lista **é** a fonte — buscar
  // um por id aqui seria uma segunda leitura para responder o que já está em mãos.
  //
  // O que este comentário dizia antes — «um site aberto por URL está sempre nela» — é
  // verdade sobre o CONJUNTO e falso sobre o INSTANTE, e essa foi a suposição que
  // quebrou: no primeiro render de um link direto a lista ainda não chegou. Quem
  // resolve isso é o `drawerPronto`, logo abaixo; a fonte continua a ser a lista.
  const inEdit = editingId ? items.find((site) => site.id === editingId) : undefined;

  /**
   * O DRAWER DE EDIÇÃO SÓ MONTA COM O REGISTRO EM MÃOS.
   *
   * `defaultValues` do `useForm` é lido no PRIMEIRO render e a `key` não muda quando o
   * dado chega — então montar antes da lista dava um «Editar site» com todos os campos
   * VAZIOS, que nunca mais se preenchia. E a tela ficava plausível: o placeholder é
   * recalculado a cada render, então as dicas apareciam certas ao lado dos valores
   * ausentes. Foi assim que o defeito atravessou 148 capturas de fidelidade sem
   * ninguém ver — o esqueleto `.txt` grava rótulo e tipo, nunca VALOR.
   *
   * Não é `key={`${id}:${carregado}`}`: essa conserta o valor mas mostra, por um
   * instante, o formulário vazio que depois se preenche sozinho — e quem estiver a ler
   * a tela nesse instante não tem como saber qual dos dois estados é o verdadeiro.
   * Não montar é honesto: até haver o que pôr no formulário, a tela é a lista.
   */
  const drawerPronto = drawer.id === 'novo' || Boolean(inEdit);

  /**
   * Link para um site que não existe mais (excluído, URL velha, tenant trocado): quando
   * a leitura TERMINOU e ele não está na lista, o parâmetro morre. Sem isto a URL fica
   * a prometer um painel que nunca abre, e o operador não tem como saber se está a
   * carregar ou se não existe.
   *
   * A condição olha `isPending`, e não `isLoading`, porque o que interessa é «a leitura
   * acabou», inclusive quando ela veio do cache.
   */
  useEffect(() => {
    if (editingId && !list.isPending && !inEdit) drawer.fechar();
  }, [editingId, list.isPending, inEdit, drawer]);

  function closeDrawer() {
    create.reset();
    edit.reset();
    drawer.fechar();
  }

  function saveEdit(site: SiteItem, values: SiteFormValues) {
    edit.mutate(
      { id: site.id, body: forUpdate(values) },
      {
        onSuccess: () => {
          setLowering(null);
          toast.mostrar(t('sites.toast.updated', { name: values.name.trim() }), {
            tone: 'sucesso',
          });
          closeDrawer();
        },
      },
    );
  }

  function onSubmit(values: SiteFormValues) {
    if (inEdit) {
      // DESCER DE NÍVEL PERGUNTA; SUBIR NÃO.
      //
      // Descer (L3 → L2 → L1) REDUZ garantia, e o diálogo diz o efeito numa frase
      // antes de gravar. Subir passa a exigir mais e não tira nada de ninguém — um
      // diálogo ali só ensinaria a clicar em «confirmar» sem ler, e é o diálogo da
      // descida que ficaria inútil no dia em que ele importa.
      //
      // A confirmação é da TELA: o servidor não tem como saber se a pessoa leu. O
      // que ele garante é a trilha (`site.posture_change`, com de/para). Esta regra
      // veio do card «Postura do site» de T21 — quando ele sair (S-c), a garantia já
      // está aqui; sair antes de ela existir é que perderia a proteção calada.
      if (lowers(inEdit.posture, values.posture)) {
        setLowering(values);
        return;
      }
      saveEdit(inEdit, values);
      return;
    }
    create.mutate(forCreate(values), {
      onSuccess: () => {
        toast.mostrar(t('sites.toast.created', { name: values.name.trim() }), { tone: 'sucesso' });
        closeDrawer();
      },
    });
  }

  function confirmDelete() {
    if (!toDelete) return;
    const name = toDelete.name;
    remove.mutate(toDelete.id, {
      onSuccess: () => {
        setToDelete(null);
        toast.mostrar(t('sites.toast.deleted', { name }), { tone: 'sucesso' });
      },
    });
  }

  function startTest(site: SiteItem) {
    test.reset();
    setTesting(site);
    test.mutate(site.id);
  }

  const columns: DataTableColumn<SiteItem>[] = [
    {
      key: 'name',
      header: t('sites.list.column.name'),
      cell: (site) => (
        <div className={s.nameCell}>
          {/* Texto puro, e não mais um `<button>` disfarçado de link: quem abre o
              site é o ícone da coluna de ações, e a linha inteira (14/09, ver o
              comentário da coluna `actions`). */}
          <span className={s.name}>{site.name}</span>
          {/* O `default` é o site que o bootstrap recria e para onde o backfill
              apontou tudo o que existia. A pílula existe para o operador não
              tentar excluí-lo e receber um 409 sem contexto. */}
          {site.is_default ? (
            <Badge tone="neutral" tip={t('sites.list.default.tip')}>
              {t('sites.list.default')}
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'slug',
      header: t('sites.list.column.slug'),
      mono: true,
      tip: t('sites.list.column.slug.tip'),
      cell: (site) => site.slug,
    },
    {
      key: 'endpoint',
      header: t('sites.list.column.endpoint'),
      mono: true,
      cell: (site) =>
        site.executor_endpoint || (
          // FR-119: endpoint vazio é «middleware não configurado» — estado
          // legítimo de um site que ainda não subiu, e o que ele custa é o site
          // ficar fora do fan-out. Dizer isso aqui é mais útil que um traço.
          <span className={s.empty} title={t('sites.list.endpoint.none.tip')}>
            {t('sites.list.endpoint.none')}
          </span>
        ),
    },
    {
      key: 'posture',
      header: t('sitePosture.fieldLabel'),
      tip: t('sitePosture.fieldTip'),
      cell: (site) => <Badge tone="neutral">{postureLabel(site.posture)}</Badge>,
    },
    {
      key: 'load',
      header: t('sites.list.column.load'),
      tip: t('sites.list.column.load.tip'),
      cell: (site) => (
        <span className={s.load}>
          {tn('sites.list.load.identities', site.identities, { n: site.identities })} ·{' '}
          {tn('sites.list.load.resources', site.resources, { n: site.resources })}
        </span>
      ),
    },
    {
      key: 'health',
      header: t('sites.list.column.health'),
      tip: t('sites.list.column.health.tip'),
      cell: (site) => <Health site={site} />,
    },
    ...(canAdmission
      ? [
          {
            key: 'admission',
            header: t('sites.list.column.admission'),
            tip: t('sites.list.column.admission.tip'),
            cell: (site: SiteItem) => <AdmissionChip summary={admissions.data?.get(site.id)} />,
          } satisfies DataTableColumn<SiteItem>,
        ]
      : []),
    {
      key: 'status',
      header: t('sites.list.column.status'),
      cell: (site) =>
        site.status === 'ativo' ? (
          <Badge tone="success">{statusLabel('ativo')}</Badge>
        ) : (
          <Badge tone="neutral" tip={t('sites.list.status.inactive.tip')}>
            {statusLabel('inativo')}
          </Badge>
        ),
    },
    {
      key: 'actions',
      header: t('sites.list.column.actions'),
      srOnlyHeader: true,
      // 160px: 96 para dois botões, 128 com o terceiro (11/09, pedido do PO), 160 com o
      // quarto — «Admissão no cluster» (aim-xsk.8), só para o admin. Largura fixa porque
      // a coluna é de ícones — deixá-la crescer faria a tabela dançar entre linhas com e
      // sem «Excluir».
      width: '160px',
      cell: (site) => (
        <div className={s.actions}>
          {/*
            O NOME deixou de ser clicável em 14/09: o mrolim padronizou as ações dos
            relatórios em ícones («não deve ser a coluna nome que é clicável»). Este
            comentário dizia o contrário — que os dois caminhos existiam de propósito,
            um para quem conhece a tela e outro para quem chega agora — e deixou de
            valer.

            O que a troca fechou, e ninguém tinha visto: o nome só era `<button>` com
            `canEdit`, e o lápis também. **Quem só lia não tinha forma nenhuma de abrir
            o site.** Agora a linha inteira abre a gaveta, com ou sem permissão, e aqui
            fica sempre UM botão — o glifo e o rótulo dizem o que a pessoa consegue
            fazer lá dentro: lápis para quem edita, lupa para quem só lê.

            UM botão, e não dois, porque a gaveta é o MESMO destino nos dois casos: um
            segundo ícone ao lado abriria exatamente a mesma coisa e não teria o que
            fazer de diferente. Se um dia ler e editar levarem a lugares diferentes —
            ou se o mrolim pedir os dois mesmo assim — são dois botões, e este
            parágrafo é o que se apaga.

            Cada botão daqui PARA a propagação. Sem isso o clique em «Testar» dispara o
            teste e abre a gaveta, porque a linha também escuta.
          */}
          <IconButton
            icon={canEdit ? 'pen-square' : 'file-search'}
            label={t(canEdit ? 'sites.list.action.edit' : 'sites.list.action.open', {
              name: site.name,
            })}
            onClick={(event) => {
              event.stopPropagation();
              drawer.abrir(site.id);
            }}
          />
          {canAdmission ? (
            <IconButton
              icon="shield"
              label={t('sites.list.action.admission', { name: site.name })}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/sites/${site.slug}/admissao`);
              }}
            />
          ) : null}
          {canTest ? (
            <IconButton
              icon="broadcast"
              label={t('sites.list.action.test', { name: site.name })}
              onClick={(event) => {
                event.stopPropagation();
                startTest(site);
              }}
            />
          ) : null}
          {/*
            O botão de excluir aparece só quando o site pode ser excluído: sem
            identidade, sem recurso e não sendo o `default`. As duas guardas de
            verdade continuam existindo (RESTRICT no banco, `409 site_padrao` na
            rota) — o que a tela evita é oferecer um botão que só pode falhar.
          */}
          {canRemove && canDelete(site) ? (
            <IconButton
              icon="trash"
              danger
              label={t('sites.list.action.delete', { name: site.name })}
              onClick={(event) => {
                event.stopPropagation();
                remove.reset();
                setToDelete(site);
              }}
            />
          ) : null}
        </div>
      ),
    },
  ];

  const filtered = term.trim().length > 0;

  return (
    <>
      <PageHeader
        title={t('sites.list.title')}
        tip={t('sites.list.purpose')}
        icon="server"
        badges={
          list.data ? (
            <Chip>{tn('sites.list.total', items.length, { n: items.length })}</Chip>
          ) : null
        }
        actions={
          canCreate ? (
            <Button icon="plus" onClick={() => drawer.abrir('novo')}>
              {t('sites.list.new')}
            </Button>
          ) : null
        }
      />

      <Card bare aria-label={t('sites.list.card.label')}>
        <DataTable
          label={t('sites.list.table.label')}
          columns={columns}
          rows={rows}
          rowKey={(site) => site.id}
          onRowClick={(site) => drawer.abrir(site.id)}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          search={term}
          onSearchChange={setTerm}
          searchPlaceholder={t('sites.list.search.placeholder')}
          filtersApplied={filtered}
          onClearFilters={() => setTerm('')}
          emptyIcon="server"
          emptyTitle={t(
            filtered ? 'sites.list.empty.filtered.title' : 'sites.list.empty.none.title',
          )}
          emptyMessage={
            filtered ? t('sites.list.empty.filtered.message') : t('sites.list.empty.none.message')
          }
          emptyAction={
            !filtered && canCreate ? (
              <Button icon="plus" onClick={() => drawer.abrir('novo')}>
                {t('sites.list.new')}
              </Button>
            ) : null
          }
        />
      </Card>

      <DrawerPresence>
        {drawer.id && drawerPronto ? (
          <SiteDrawer
            // Recria o formulário a cada site aberto: `defaultValues` só é lido no
            // primeiro render do `useForm`.
            key={drawer.id}
            open
            site={inEdit}
            postures={postures}
            templateDefault={templateDefault}
            error={inEdit ? edit.error : create.error}
            pending={inEdit ? edit.isPending : create.isPending}
            onSubmit={onSubmit}
            onClose={closeDrawer}
          />
        ) : null}
      </DrawerPresence>

      <ConfirmDialog
        open={Boolean(lowering)}
        destructive
        title={t('sitePosture.lowerTitle', {
          posture: postureLabel(lowering?.posture ?? 'artifact_binding'),
        })}
        message={t('sitePosture.lowerEffect')}
        confirmLabel={t('sitePosture.lowerConfirm')}
        cancelLabel={t('sitePosture.lowerCancel')}
        loading={edit.isPending}
        onConfirm={() => {
          if (inEdit && lowering) saveEdit(inEdit, lowering);
        }}
        onCancel={() => setLowering(null)}
      />

      <ConfirmDialog
        open={Boolean(toDelete)}
        destructive
        title={t('sites.delete.title', { name: toDelete?.name ?? '' })}
        message={t('sites.delete.message')}
        confirmLabel={t('sites.delete.confirm')}
        loading={remove.isPending}
        error={
          // A recusa fala pelo CÓDIGO, não por «deu erro». `site_em_uso` diz QUANTAS
          // identidades e QUANTOS recursos (o domínio manda os dois no `data` desde a
          // P1; o contrato só passou a declará-los no F1-b), e `site_padrao` diz que o
          // padrão não se exclui. A frase genérica que morava aqui escondia as duas —
          // e a tela não tinha o que dizer depois de o operador clicar.
          // SEM `escrita: true`, e não é economia de argumento. Os dois códigos que esta
          // recusa produz já declaram `gravacao: 'nada'` (`site_em_uso`, `site_padrao`),
          // então a frase «Nada foi gravado.» sai de qualquer forma — para eles é o
          // servidor que garante. Ligar o `escrita` aqui estenderia a promessa a TODO
          // erro que caia neste diálogo, inclusive `sem_conexao` e `tempo_esgotado`,
          // onde ninguém sabe se o `DELETE` chegou a ser aplicado: seria a tela a
          // afirmar sobre gravação o que ela não tem como saber.
          remove.error ? errorView(remove.error).texto : undefined
        }
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />

      {testing ? (
        <SiteConnectionDialog
          open
          site={testing.name}
          trustDomainReadAt={testing.trust_domain_read_at}
          result={test.data}
          error={test.error}
          pending={test.isPending}
          onClose={() => {
            setTesting(null);
            test.reset();
          }}
        />
      ) : null}
    </>
  );
}

/**
 * A coluna «saúde»: a última sondagem PERSISTIDA, com três estados.
 *
 * `null` é «nunca testado», e é diferente de «testado e falhou» — a distinção
 * existe porque um site recém-cadastrado não é um site com problema, e pintá-lo
 * de vermelho ensinaria o operador a ignorar vermelho.
 */
function Health({ site }: { site: SiteItem }) {
  const probe = site.last_probe;
  if (!probe) {
    return <span className={s.empty}>{t('sites.list.health.never')}</span>;
  }
  const when = dataHour(probe.at);
  if (probe.ok) {
    return (
      <Badge
        tone="success"
        tip={t('sites.list.health.ok.tip', { when: when ?? '', v: probe.version })}
      >
        {t('sites.list.health.ok')}
      </Badge>
    );
  }
  // O código do executor vai intacto: é o vocabulário do envelope, e um segundo
  // nome para o mesmo erro tornaria a tela e o log incomparáveis.
  return (
    <Badge tone="danger" tip={t('sites.list.health.failed.tip', { when: when ?? '' })}>
      {probe.code}
    </Badge>
  );
}
